<template>
    <div class="text-xs mb-4">
        <div v-if="form" class="grid grid-cols-2 mb-2">
            <div class="text-left">
                <button
                    v-if="enableExport"
                    class="items-center px-4 py-1 bg-gray-800 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-gray-700 active:bg-gray-900 focus:outline-none focus:border-gray-900 focus:shadow-outline-gray transition ease-in-out duration-150"
                    @click="downloadCsv"
                >
                    Exportar CSV
                </button>
            </div>
        </div>
        <div
            v-if="form && (enableSearch || (filters && filters.length))"
            class="mb-6 grid grid-cols-12 gap-4 relative"
        >
            <search-field
                v-if="form && enableSearch"
                v-model="form.q"
                class="w-full col-span-10 lg:col-span-11"
                @reset="reset"
                @input="inputSearch"
            />
            <div
                v-if="filters && filters.length"
                class="col-span-1 flex justify-center items-center absolute right-8"
                style="top: -5px"
            >
                <div
                    class="rounded-full bg-yellow-400 h-12 w-12 flex items-center justify-center cursor-pointer"
                    @click="showModalFilter = true"
                    v-tippy
                    content="Filtro"
                >
                    <i class="fas fa-filter text-black"></i>
                </div>
            </div>
        </div>

        <div
            v-if="filters && filters.length"
            class="flex flex-wrap col-span-12 my-1 items-baseline"
        >
            <div
                v-for="(item, key) in filterQueryString"
                :key="key"
                v-show="parserValueFilter(key, item)"
                class=" mr-5 whitespace-no-wrap text-gray-500 text-sm px-2 py-1 text-center max-w-max bg-gray-200 flex items-center justify-between mt-2 max-w"
                :class="Array.isArray(item) ? 'rounded-xl' : 'rounded-full'"
            >
                <span>
                    {{ getFilterNameByKey(key) }} :
                    <b v-html="parserValueFilter(key, item)"></b>
                </span>
                <i
                    class="fas fa-times-circle ml-2 cursor-pointer"
                    @click="removeFilter(key)"
                ></i>
            </div>
        </div>
        <div class="flex items-end justify-end mt-4 space-x-2">
            <slot name="buttons"> </slot>
        </div>

        <div class="bg-white overflow-x-auto">
            <table
                v-bind:class="tableClasses"
                class="w-full whitespace-no-wrap "
            >
                <thead>
                    <tr class="font-bold text-left">
                        <th
                            class="px-4 pt-6 py-4 whitespace-nowrap cursor-pointer"
                            v-if="multiSelect"
                        >
                            <input
                                type="checkbox"
                                v-model="selectedAll"
                                @click="selectAll"
                                class="form-checkbox h-5 w-5 text-green-600"
                            />
                        </th>
                        <th
                            v-for="header in columns"
                            class="px-4 pt-6 py-4 whitespace-nowrap cursor-pointer"
                            v-bind:class="header.className || ''"
                            :key="header.key"
                            @click="sort(header)"
                        >
                            {{ header.text }}
                            <i
                                class="fas text-xs"
                                v-bind:class="{
                                    'fa-arrow-up':
                                        form.sort.split(':')[1] === 'asc',
                                    'fa-arrow-down':
                                        form.sort.split(':')[1] === 'desc'
                                }"
                                v-if="
                                    form.sort &&
                                        header.sortable &&
                                        header.key === form.sort.split(':')[0]
                                "
                            />
                        </th>
                        <th
                            class="px-4 pt-6 py-4 whitespace-nowrap "
                            v-if="icon && hasIcon"
                        ></th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(item, idx) in getList.list"
                        :key="`${item.id}${Math.random()}`"
                        :class="{
                            'cursor-pointer': hasCursor(item.icon),
                            'bg-gray-100 hover:bg-gray-200 focus-within:bg-gray-200':
                                idx % 2 === 0,
                            'bg-white hover:bg-gray-200 focus-within:bg-gray-200':
                                idx % 2 !== 0
                        }"
                    >
                        <td v-if="multiSelect" class="px-4 py-6">
                            <input
                                type="checkbox"
                                v-model="item.selected"
                                class="form-checkbox h-5 w-5 text-green-600"
                            />
                        </td>
                        <td
                            v-for="(column, index) in columns"
                            :key="index"
                            class="px-4 py-6 cursor-default"
                        >
                            <slot
                                :name="column.key"
                                :index="idx"
                                :item="item"
                                >{{ item[column.key] }}</slot
                            >
                        </td>
                        <td
                            @click="$emit('item-selected', item)"
                            class="w-px"
                            v-if="icon && hasIcon"
                            content="Ver mais"
                            v-tippy
                        >
                            <p class="px-4 flex items-center">
                                <i
                                    class="fas"
                                    v-bind:class="item.icon || icon"
                                />
                            </p>
                        </td>
                    </tr>
                    <tr v-if="getList.list.length === 0">
                        <td
                            class="bg-gray-50 h-28 m-0 pl-6 overflow-hidden relative"
                            v-bind:colspan="
                                multiSelect
                                    ? columns.length + 1
                                    : columns.length
                            "
                        >
                            <span class="font-bold text-lg text-gray-400">{{
                                emptyMessage
                            }}</span>
                            <div class="absolute right-0 -mt-20 -mr-3">
                                <i
                                    class="far fa-question-circle align-middle text-gray-100 fa-12x"
                                />
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div
            class="sm:flex-1 sm:flex sm:items-center mt-5"
            :class="enablePageSize ? 'justify-between' : 'justify-center'"
            v-if="enableDisplay"
        >
            <page-size
                v-if="enablePageSize"
                v-model="form['size']"
                @input="getUpdateData"
            />
            <paginate
                v-if="enablePages && data.pages"
                :route-name="routeName"
                @click="paginate"
                :pagination="data.pages"
            />
        </div>
        <modal-filter
            :key="keyModal"
            :show="showModalFilter"
            max-width="2xl"
            closeable="true"
            :filters="filters"
            :form.sync="form"
            @close="showModalFilter = false"
            @reset="reset"
            @apply="apply"
        />
    </div>
</template>

<script>
import _ from "lodash";
import SearchField from "./SearchField";
import SearchFilter from "./SearchFilter";
import Paginate from "./Paginate";
import PageSize from "./PageSize.vue";
import eventBus from "../../Layouts/eventBus.vue";
import ModalFilter from "./ModalFilter.vue";
import moment from "moment";

export default {
    components: {
        SearchField,
        SearchFilter,
        Paginate,
        PageSize,
        ModalFilter
    },
    props: {
        hasIcon: {
            type: Boolean,
            default: true
        },
        hasLine: {
            type: Boolean,
            default: true
        },
        columns: {
            type: Array,
            default: null
        },
        createLink: {
            type: String,
            default: null
        },
        data: {
            type: Object,
            default: null
        },
        enablePages: {
            type: Boolean,
            default: true
        },
        enableSearch: {
            type: Boolean,
            default: true
        },
        searchFullWithButton: {
            type: Boolean,
            default: false
        },
        enablePageSize: {
            type: Boolean,
            default: true
        },
        filters: {
            type: Array,
            default: null
        },
        icon: {
            type: String | Boolean,
            default: "fa-chevron-right"
        },
        id: {
            type: String,
            default: "id"
        },
        multiSelect: {
            type: Boolean,
            default: false
        },
        params: {
            type: Object,
            default: null
        },
        routeName: String,
        tableClasses: {
            type: String,
            default: null
        },
        emptyMessage: {
            type: String,
            default: "Nenhum registro encontrado"
        },
        enableExport: {
            type: Boolean,
            default: false
        },
        parent: {
            type: String,
            default: null
        },
        enableDisplay: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        filterQueryString() {
            const queryString = {};
            for (let key in this.queryString) {
                queryString[key] = this.b64ToUtf8(this.queryString[key]);
            }
            return queryString;
        },
        hasItemSelectedListener() {
            let has = false;
            if (this.$listeners && this.$listeners["item-selected"]) {
                has = true;
            }
            return has;
        },
        getList: {
            get: function() {
                return this.data && this.data.list ? this.data : { list: [] };
            }
        }
    },
    data() {
        return {
            queryString: {},
            keyModal: 0,
            initComponent: false,
            item: null,
            form: null,
            oldForm: {},
            search: "",
            entityName: "",
            selectedAll: false,
            selectedCount: 0,
            showModalFilter: false
        };
    },
    watch: {
        form: {
            handler: _.debounce(function() {
                this.initComponent && this.updateQueryString();
                if (!this.initComponent) {
                    this.initComponent = true;
                    return;
                }
            }, 700),
            deep: true
        }
    },
    created: function() {
        if (this.columns === null) {
            this.columns = Array.from(this.getList.keys());
        }
        this.createFormData();
        this.updateQueryString();
        this.keyModal++;
    },
    updated: function() {
        let totalSelected = 0;
        this.getList.list.forEach(item => {
            if (item.selected) {
                totalSelected++;
            }
        });
        this.selectedAll = totalSelected === this.getList.list.length;
    },
    methods: {
        inputSearch: _.debounce(function() {
            this.getUpdateData();
        }, 700),
        templateDate(date){
            if (!date.from && !date.to) return "";
            const format = 'DD/MM/YYYY'
            let dateFrom = date.from
                ? moment(date.from).format(format)
                : "";
            let dateTo = date.to
                ? moment(date.to).format(format)
                : "";
            let text =
                "Período selecionado " +
                (dateFrom ? "de " + dateFrom : "") +
                (dateTo ? " até " + dateTo : "");
            return text;
        },
        getUpdateData() {
            let query = _.pickBy(this.form);
            this.$inertia.visit(
                this.route(
                    this.routeName,
                    Object.keys(query).length ? query : { clear: 1 }
                ),
                {
                    preserveState: true
                }
            );
        },
        b64ToUtf8(str) {
            try {
                return JSON.parse(decodeURIComponent(escape(window.atob(str))));
            } catch (error) {
                return str;
            }
        },
        parserValueFilter: function(key, value) {
            if (this.filters === null || this.filters.length === 0) {
                return value;
            }

            let filter = this.filters.find(f => f.key === key);

            if (value === null || value === undefined) {
                return "";
            }

            if (filter === undefined) {
                return value;
            }

            switch (filter.type) {
                case "multipleOptions":
                    const html = isDefine =>
                        `<div class="text-left mx-2 flex">${
                            isDefine ? "Definir" : "Excluir"
                        } | `;
                    value = this.b64ToUtf8(value);
                    if (filter.key !== "accounts") {
                        return value.reduce(
                            (acc, item) =>
                                acc +
                                `${html(
                                    item.isDefine
                                )} <span class="ml-1 truncate w-48">${
                                    item.name
                                }</span></div>`,
                            ""
                        );
                    }
                    return value.reduce(
                        (acc, item) =>
                            acc +
                            `${html(
                                item.isDefine
                            )} <span class="ml-1 truncate w-44">${
                                item.name
                            }</span> | Cc: ${item.number}-${item.digit}</div>`,
                        ""
                    );
                case "autocomplete":
                    return value.name || "";
                case "date_interval":
                    return this.templateDate(value);
                case "date_interval2":
                    return this.templateDate(value);
                case "number_interval":
                    if(!value.number_to && !value.number_from) return ''
                    if (parseFloat(value.number_to) === 0) {
                        return (
                            "Valor de " +
                            (value.isPrecision === false ? value.number_from ?? 0 :
                            parseFloat(value.number_from ?? 0).toLocaleString(
                                "pt-BR",
                                {
                                    minimumFractionDigits: 2,
                                }
                            ))
                        );
                    } else {
                        return (
                            "Valor de " +
                            (value.isPrecision === false ? value.number_from ?? 0 :
                            parseFloat(value.number_from ?? 0).toLocaleString(
                                "pt-BR",
                                {
                                    minimumFractionDigits: 2
                                }
                            )) +
                            " até " +
                            (value.isPrecision === false ? value.number_to ?? 0 :
                            parseFloat(value.number_to ?? 0).toLocaleString(
                                "pt-BR",
                                {
                                    minimumFractionDigits: 2
                                }
                            ))
                        );
                    }
                case "datepicker":
                    return this.$options.filters.smallDate(value);
                case "text":
                    return value;
                case "dropdown":
                    let option = filter.options.find(o => o.id == value);
                    if (option && option.name) {
                        return option.name;
                    }
                    return value;
                case "checkbox_icon":
                    let keyParsed = [];
                    value.forEach(v => {
                        let option = filter.options.find(o => o.id === v);
                        keyParsed.push(option.name);
                    });

                    if (value.length === 0) {
                        this.removeFilter(key);
                    }
                    return keyParsed.join(", ");

                default:
                    return value;
            }
        },
        getFilterNameByKey: function(key) {
            if (this.filters === null || this.filters.length === 0) {
                return key;
            }
            let item = this.filters.find(filter => filter.key === key);
            if (item) {
                return item.text;
            } else if (key === "q") {
                return "Busca";
            }
            return key;
        },
        updateQueryString() {
            this.$forceUpdate();
            if (!this.form.date?.from && !this.form.date?.to) {
                delete this.form.date;
            }
            this.queryString = _.pickBy(this.form);
            this.queryString = _.omit(this.queryString, [
                "sort",
                "page",
                "clear",
                "divergence",
                "size",
                "customer_id",
                "0",
                "type"
            ]);
        },
        removeFilter(key) {
            let customerId;
            if (this.form.customer_id !== undefined) {
                customerId = this.form.customer_id;
            }
            let validateFilterExist = this.filters.find(f => f.key === key);

            this.form[key] = null;
            delete this.form[key];
            delete this.form.key;

            this.queryString[key] = null;
            delete this.queryString[key];
            delete this.queryString.key;

            this.$forceUpdate();

            if (validateFilterExist) {
                eventBus.$emit("clearFilter", validateFilterExist);
            }

            this.updateQueryString();
            if (customerId !== undefined) {
                this.$inertia.visit(
                    this.route(
                        this.routeName,
                        Object.keys(this.queryString).length
                            ? `'customer_id='${customerId}${this.queryString}`
                            : `'customer_id='${customerId}"&clear=1"`
                    ),
                    {
                        preserveState: true
                    }
                );
            } else {
                this.$inertia.visit(
                    this.route(
                        this.routeName,
                        Object.keys(this.queryString).length
                            ? this.queryString
                            : { clear: 1 }
                    ),
                    {
                        preserveState: false
                    }
                );
            }
        },
        apply() {
            this.form.page = 1;
            let query = _.pickBy(this.form);
            this.showModalFilter = false;
            this.$inertia.visit(this.route(this.routeName, query), {
                preserveState: true
            });

            this.filters.forEach(filter => {
                try {
                    this.getValue(filter);
                } catch (err) {}
            });
        },
        hasCursor(icon = "") {
            if (
                this.parent === "extract" &&
                this.existIcon(icon) &&
                this.hasItemSelectedListener
            ) {
                return true;
            } else if (this.parent === "extract" && !this.existIcon(icon)) {
                return false;
            } else if (this.hasItemSelectedListener) {
                return true;
            } else {
                return false;
            }
        },
        existIcon(icon) {
            return (
                icon !== undefined &&
                icon !== null &&
                icon !== "" &&
                icon !== "-"
            );
        },
        downloadCsv() {
            const queryString = window.location.href.split("?")[1] || "";
            const url =
                window.location.href
                    .split("?")[0]
                    .split("/")
                    .pop() ?? null;
            const endPoint = `json/${url}?export=true&${queryString}`;
            fetch(endPoint).then(res => {
                res.json()
                    .then(json => {
                        const exportData = json["exportData"] ?? null;
                        if (exportData === null) {
                            this.$toast.open({
                                message:
                                    "Exportação de dados não disponível para esta tabela.",
                                type: "warning",
                                duration: 5000,
                                position: "top-right"
                            });
                            return false;
                        }
                        if (json["list"].length === 0 || exportData === null) {
                            this.$toast.open({
                                message:
                                    "Não existem dados a serem exportados.",
                                type: "warning",
                                duration: 5000,
                                position: "top-right"
                            });
                            return false;
                        }

                        let csv = ``;
                        json["exportData"]["title"].forEach(row => {
                            csv += `${row}\n`;
                        });
                        csv += `\n\n`;
                        let title = "";
                        json["exportData"]["columns"].forEach(column => {
                            title += `${column.text};`;
                        });
                        csv += `${title.substring(0, title.length - 1)}\n`;

                        json["exportData"]["rows"].forEach(row => {
                            let line = "";
                            json["exportData"]["columns"].forEach(column => {
                                if (row[column.key] !== undefined) {
                                    line += `${row[column.key]};`;
                                }
                            });
                            csv += `${line.substring(0, line.length - 1)}\n`;
                        });

                        const anchor = document.createElement("a");
                        anchor.href =
                            "data:text/csv;charset=utf-8," +
                            encodeURIComponent(csv);
                        anchor.target = "_blank";
                        anchor.download = `exportacao-${this.id}-${Math.random()
                            .toString(36)
                            .substr(2, 9)}.csv`;
                        anchor.click();

                        this.$toast.open({
                            message: "Arquivo gerado com sucesso.",
                            type: "success",
                            duration: 5000,
                            position: "top-right"
                        });
                    })
                    .catch(e => {
                        this.$toast.open({
                            message:
                                "Exportação de dados não disponível para esta tabela.",
                            type: "warning",
                            duration: 5000,
                            position: "top-right"
                        });
                    });
            });
        },
        formHasChanged: function() {
            for (const o in this.form) {
                if (o === "interval" && this.oldForm[o] !== this.form[o]) {
                    if (typeof this.form.date_to !== "undefined")
                        this.clearFilter("date_to");
                    if (typeof this.form.date_from !== "undefined")
                        this.clearFilter("date_from");
                } else if (
                    (o === "date_from" || o === "date_to") &&
                    this.oldForm[o] !== this.form[o]
                ) {
                    if (typeof this.form?.interval !== "undefined")
                        this.clearFilter("interval");
                }

                if (
                    o === "date_from" &&
                    this.form.date_to !== null &&
                    this.form.date_to !== ""
                ) {
                    if (this.form.date_from > this.form.date_to) {
                        this.form.date_from = this.oldForm.date_from;
                        this.$toast.open({
                            message:
                                "Data inicial não pode ser maior que a data final.",
                            type: "warning",
                            duration: 5000,
                            position: "top-right"
                        });

                        if (typeof this.form.date_to !== "undefined")
                            this.clearFilter("date_to");
                    }
                }

                if (this.oldForm[o] !== this.form[o] && o !== "page") {
                    Object.assign(this.oldForm, this.form);
                    return true;
                }
            }
            return false;
        },
        clearFilter(key) {
            let validateFilterExist = this.filters.find(f => f.key === key);

            this.form[key] = null;
            delete this.form[key];
            delete this.form.key;

            this.$forceUpdate();

            if (validateFilterExist) {
                eventBus.$emit("clearFilter", validateFilterExist);
            }
        },
        createFormData: function() {
            this.form = {
                ...this.params,
                ...this.$page.props.queryString
            };
            Object.assign(this.oldForm, this.form);
        },
        reset() {
            for (let item in this.filters) {
                eventBus.$emit("clearFilter", item);
            }
            this.$forceUpdate();
            this.$inertia.visit(this.route(this.routeName), {
                preserveState: false
            });
        },
        sort(column) {
            if (!column.sortable) {
                return;
            }
            let sort = this.form.sort.split(":");
            let sortColumn = column.sortField || column.key;

            sort[1] =
                sort[0] === sortColumn && sort[1] === "asc" ? "desc" : "asc";
            sort[0] = sortColumn;
            this.form.sort = sort.join(":");
            this.apply()
        },
        paginate: function(page) {
            this.form.page = page;
            let query = _.pickBy(this.form);
            this.$inertia.visit(
                this.route(
                    this.routeName,
                    Object.keys(query).length ? query : null
                ),
                {
                    preserveState: true
                }
            );
        },
        selectAll: function() {
            this.selectedAll = !this.selectedAll;
            this.getList.list.forEach(item => {
                item.selected = this.selectedAll;
            });
            this.$forceUpdate();
        }
    }
};
</script>
